/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { buffers, EventChannel, eventChannel, Task } from 'redux-saga';
import { call, cancel, flush, put, takeLatest } from 'redux-saga/effects';

import { getAuthToken } from '~/app/authStorage';
import { config } from '~/app/config';
import { gameLogger } from '~/app/logger';
import {
  sagaGameReceiver,
  sagaGameSender,
  setConnected,
  startDebug,
  StartDebugParameters,
  startFree,
  startTournament,
  startTraining,
  StartTrainingParameters,
} from '~/game';
import { GameType } from '~/server/src/dtos';
import { Controller, View } from '~/server/src/interfaces';
import { StateMachineValue } from '~/server/src/tmmodel';
import {
  isRestoreGameParameters,
  rejoinGame,
  restoreGame,
  RestoreGameParameters,
  sagaRestoreGame,
  sagaSetupDebug,
  sagaSetupFree,
  sagaSetupTournament,
  sagaSetupTraining,
} from '~/setup';
import type { SetupChannelEvent } from '~/setup/channelEvent';
import sagaRejoinGame from '~/setup/rejoin/sagaRejoinGame';
import { actions as setupActions } from '~/setup/state';
import { callSaga, forkSaga } from '~/setup/utils/sagaEffects';

import { setServerTime, setUserId } from './connectionStore';
import ControllerLocalWrapper from './controllerLocalWrapper';
import { ensureControllerWrapperConnected } from './controllerRemoteWrapper';

type ClientView = Pick<View, 'receiveMsg'>;

const controllerChannel = (onViewCreated: (view: ClientView) => void) => {
  return eventChannel<SetupChannelEvent>((emit) => {
    // handler for messages received by the controller which will be emitted into the saga context
    const view: ClientView = {
      receiveMsg: async (state: StateMachineValue, params: unknown) => {
        emit({ state, params });
      },
    };
    onViewCreated(view);

    return () => {
      gameLogger.debug('remote controller event channel terminated');
      /* unsubscribe & cleanup */
    };
  }, buffers.expanding<any>(10)); // dynamic buffer which captures everything and prevents message loss
};

type MainSagaParams = void | StartTrainingParameters | RestoreGameParameters;

// https://gitlab.com/plusbyte/turfmaster/de.plusbyte.azaspiele.turfmaster.server/wikis/home
function* mainSaga(action: PayloadAction<MainSagaParams>) {
  gameLogger.info({ actionType: action.type }, 'main saga started');

  // create channel to listen to external controller events
  let view: ClientView | undefined = undefined;
  const channel: EventChannel<SetupChannelEvent> = yield call(
    controllerChannel,
    (v: ClientView) => {
      view = v;
    },
  );

  // make sure channel is empty
  yield flush(channel);

  // view creation did not work so quit
  if (!view) {
    gameLogger.warn('main saga terminated because view creation failed');
    return;
  }

  if (action.type === rejoinGame.type) {
    try {
      const token = getAuthToken();
      if (!token) {
        gameLogger.warn('mainSaga: rejoin requested without token, dispatching restore failure');
        yield put(setupActions.setRestoreState('error'));
        return;
      }

      const rejoinLocalController: Controller = new ControllerLocalWrapper(GameType.Training);

      const controllerWrapper = yield call(ensureControllerWrapperConnected, {
        onTokenExpired: () => {
          gameLogger.info('mainSaga: Token expired during rejoin');
        },
        // Probe-only usage: avoid auto-rejoin handlers and token monitoring while on start/menu screens.
        purpose: 'probe',
      });
      const rejoinRemoteController = controllerWrapper?.getController() ?? null;

      if (!rejoinRemoteController) {
        throw new Error('Failed to create remote controller for rejoin');
      }

      const rejoinSuccessful = yield call(
        sagaRejoinGame,
        view,
        rejoinLocalController,
        rejoinRemoteController,
        channel,
      );

      rejoinLocalController.closeConnection?.();

      // Probe-only: never enter the full game loop from start/menu routes.
      // The rejoin button reads setup.rejoinState and will navigate to the appropriate route.
      gameLogger.debug(
        { rejoinSuccessful },
        'mainSaga: rejoin probe completed; disconnecting controller',
      );
      controllerWrapper?.disconnect();
    } finally {
      gameLogger.debug('main saga terminated');
      channel.close();
    }
    return;
  }

  // new controller instance for each game start
  let controller: Controller | null;

  // offline game start training or restore training
  if (
    action.type === startTraining.type ||
    (action.type === restoreGame.type &&
      isRestoreGameParameters(action.payload) &&
      action.payload.mode === 'training')
  ) {
    gameLogger.info('mainSaga: using local controller wrapper in training mode');
    controller = new ControllerLocalWrapper(GameType.Training);
  }
  // online game
  else {
    const token = getAuthToken();
    if (!token) {
      gameLogger.warn(
        {
          hasToken: !!token,
          useMockedAuth: config.useMockedAuthToken,
          mockedToken: config.useMockedAuthToken ? 'enabled' : 'disabled',
        },
        'mainSaga: online restore/start without token, dispatching restore failure',
      );
      yield put(setupActions.setRestoreState('error'));
      channel.close();
      return;
    }

    gameLogger.info({ apiUrl: config.baseUrl }, 'mainSaga: using remote controller');

    // Create or get existing controller wrapper
    const apiUrl = config.baseUrl;
    if (!apiUrl) {
      throw new Error('VITE_API environment variable is not set');
    }

    const controllerWrapper = yield call(ensureControllerWrapperConnected, {
      onTokenExpired: () => {
        gameLogger.info('mainSaga: Token expired, attempting reconnection');
      },
      onReconnected: () => {
        gameLogger.info('mainSaga: Reconnected successfully');
      },
    });
    const controllerRemote = controllerWrapper?.getController() ?? null;

    if (!controllerRemote) {
      throw new Error('Failed to create remote controller');
    }

    // promise gets resolved only after controller is fully connected
    // safe-guard so that we do not set userId and serverTime before they are available
    // and prevents premature navigation to game screen
    yield call([controllerRemote, controllerRemote.waitForConnection]);
    gameLogger.info('mainSaga: Remote controller connection established');

    if (controllerRemote.userId) {
      yield put(setUserId(controllerRemote.userId));
    }
    if (controllerRemote.serverTime) {
      yield put(setServerTime(controllerRemote.serverTime));
    }

    controller = controllerRemote;
  }

  if (!controller) {
    gameLogger.error('mainSaga: controller was not initialized');
    channel.close();
    return;
  }

  const activeController: Controller = controller;

  // send actions from user interface as state-messages to controller
  const sender: Task = yield* forkSaga(sagaGameSender, activeController);

  // Buffered controller events captured during setup handshakes (e.g. early STARTGAME/STARTROUND).
  // sagaGameReceiver will apply them to Redux before continuing with live controller events.
  let setupReplayEvents: any[] = [];

  try {
    // training startup with NEWGAME event. returns instant after starting the game
    if (action.type === startTraining.type) {
      const params = action.payload as StartTrainingParameters;
      if (params.userId) {
        yield put(setUserId(params.userId));
      }

      yield* callSaga(sagaSetupTraining, view, activeController, params, channel);
    } else if (action.type === restoreGame.type) {
      const params = action.payload as RestoreGameParameters;
      if (params.userId) {
        yield put(setUserId(params.userId));
      }

      const restoreResult = (yield* callSaga(
        sagaRestoreGame,
        view,
        activeController,
        params,
        channel,
      )) as {
        ok: boolean;
        replayEvents: any[];
      };
      if (!restoreResult.ok) return;
      setupReplayEvents = restoreResult.replayEvents;
    } else if (action.type === startFree.type) {
      // free startup with GETGAMES, NEWGAME / JOINGAME. therefore a long running task with lobby handling etc.

      const setupFreeResult = (yield* callSaga(sagaSetupFree, view, activeController, channel)) as {
        ok: boolean;
        replayEvents: any[];
      };
      if (!setupFreeResult.ok) return;
      setupReplayEvents = setupFreeResult.replayEvents;

      // Important: do NOT flush the channel here.
      // STARTGAME / STARTROUND can arrive during the join handshake and must not be dropped.
      // sagaGameReceiver ignores GETGAMES / GETTOURNAMENTS lobby events anyway.
    } else if (action.type === startTournament.type) {
      const setupTournamentResult = (yield* callSaga(
        sagaSetupTournament,
        view,
        activeController,
        channel,
      )) as {
        ok: boolean;
        replayEvents: any[];
      };
      if (!setupTournamentResult.ok) return;
      setupReplayEvents = setupTournamentResult.replayEvents;

      // Important: do NOT flush the channel here (see comment above).
    } else if (action.type === startDebug.type) {
      yield* callSaga(
        sagaSetupDebug,
        view,
        activeController,
        channel,
        action.payload as StartDebugParameters,
      );
    }

    // listen and react to controller actions in an endless loop

    yield* callSaga(sagaGameReceiver, activeController, channel, setupReplayEvents);
  } finally {
    gameLogger.debug('main saga terminated');

    channel.close();
    // Note: ControllerRemoteWrapper manages its own connection lifecycle
    // Only close if it's a local controller
    if (activeController && (!activeController.getType || activeController.getType() === 'local')) {
      activeController.closeConnection?.();
    }
    yield cancel(sender);

    yield put(setConnected(false));
  }
}

function* mainSagaRoot() {
  // takeLatest: one mainSaga at a time. A new start or rejoin cancels the in-flight one.
  // This prevents two concurrent game loops (e.g. startFree then rejoinGame) which would fork
  // two sagaGameSenders and cause duplicate chat messages (both handle sendMessage).
  // takeLeading for rejoinGame is no longer separate; rejoinGame in takeLatest cancels the
  // current run (e.g. from startFree) so only the rejoin loop runs.
  yield takeLatest(
    [
      startTraining.type,
      startFree.type,
      restoreGame.type,
      startTournament.type,
      startDebug.type,
      rejoinGame.type,
    ],
    mainSaga,
  );
}

export default mainSagaRoot;
